/**
 * Catalog Archive - Center-fold page turn, mobile half-page view, zoom tool
 */
(function () {
  'use strict';

  var PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDFJS_WORKER_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  var TURN_MS = 550;
  var MOBILE_BP = 768;
  var MAGNIFIER_SIZE = 260;
  var MAGNIFIER_ZOOM = 3;

  function easeInOut(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function isMobile() { return window.innerWidth <= MOBILE_BP; }

  function CatalogArchive(el) {
    this.el = el;
    this.shelfView = el.querySelector('[data-catalog-shelf]');
    this.readerView = el.querySelector('[data-catalog-reader]');
    this.canvasWrap = el.querySelector('[data-catalog-canvas-wrap]');
    this.canvas = el.querySelector('[data-catalog-canvas]');
    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
    this.pageInfo = el.querySelector('[data-catalog-page-info]');
    this.pageSlider = el.querySelector('[data-catalog-slider]');
    this.btnPrev = el.querySelector('[data-catalog-prev]');
    this.btnNext = el.querySelector('[data-catalog-next]');
    this.btnFirst = el.querySelector('[data-catalog-first]');
    this.btnLast = el.querySelector('[data-catalog-last]');
    this.btnZoom = el.querySelector('[data-catalog-zoom]');
    this.readerTitleEl = el.querySelector('[data-catalog-reader-title]');
    this.loadingEl = el.querySelector('[data-catalog-loading]');

    this.pdfDoc = null;
    this.currentPage = 1;
    this.totalPages = 0;
    this.rendering = false;
    this.pendingPage = null;
    this.turning = false;
    this.lastViewport = null;

    // Mobile half-page state
    this.mobileMode = false;
    this.mobileHalf = 0; // 0 = left, 1 = right
    this.mobileTotalVirtual = 0;
    this.mobileVirtualPage = 0;

    // Zoom/magnify state
    this.zoomActive = false;
    this.magnifier = null;
    this.hiResCanvas = null;

    this._initBooks();
    this._initControls();
    this._initZoom();

    var self = this;
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (self.turning) return;
        var wasMobile = self.mobileMode;
        self.mobileMode = isMobile();
        if (self.pdfDoc && self.readerView && self.readerView.classList.contains('is-active')) {
          if (wasMobile !== self.mobileMode) {
            // Mode changed - re-sync page position
            if (self.mobileMode) {
              self.mobileHalf = 0;
              self.mobileVirtualPage = (self.currentPage - 1) * 2;
            } else {
              self.currentPage = Math.floor(self.mobileVirtualPage / 2) + 1;
              if (self.currentPage > self.totalPages) self.currentPage = self.totalPages;
            }
          }
          self._renderCurrentView();
        }
      }, 150);
    });
  }

  CatalogArchive.prototype._initBooks = function () {
    var self = this;
    this.el.querySelectorAll('[data-catalog-book]').forEach(function (book) {
      book.addEventListener('click', function () {
        self._openBook(book.dataset.catalogPdf, book.dataset.catalogTitle || 'Catalog');
      });
    });
  };

  CatalogArchive.prototype._initControls = function () {
    var self = this;
    if (this.btnPrev) this.btnPrev.addEventListener('click', function () { self._changePage(-1); });
    if (this.btnNext) this.btnNext.addEventListener('click', function () { self._changePage(1); });
    if (this.btnFirst) this.btnFirst.addEventListener('click', function () { self._goToFirst(); });
    if (this.btnLast) this.btnLast.addEventListener('click', function () { self._goToLast(); });

    if (this.pageSlider) {
      this.pageSlider.addEventListener('input', function () {
        var v = parseInt(self.pageSlider.value, 10);
        if (self.mobileMode) {
          self.mobileVirtualPage = v - 1;
          self.currentPage = Math.floor(self.mobileVirtualPage / 2) + 1;
          self.mobileHalf = self.mobileVirtualPage % 2;
          self._renderCurrentView();
        } else {
          self._goToPage(v);
        }
      });
    }

    var prevZone = this.el.querySelector('[data-catalog-zone-prev]');
    var nextZone = this.el.querySelector('[data-catalog-zone-next]');
    if (prevZone) prevZone.addEventListener('click', function () { self._changePage(-1); });
    if (nextZone) nextZone.addEventListener('click', function () { self._changePage(1); });

    document.addEventListener('keydown', function (e) {
      if (!self.readerView || !self.readerView.classList.contains('is-active')) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); self._changePage(-1); }
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') { e.preventDefault(); self._changePage(1); }
      else if (e.key === 'Escape') { self._closeReader(); }
    });

    var backBtn = this.el.querySelector('[data-catalog-back]');
    if (backBtn) backBtn.addEventListener('click', function () { self._closeReader(); });
  };

  /* =============================================
     ZOOM / MAGNIFIER
     ============================================= */
  CatalogArchive.prototype._initZoom = function () {
    var self = this;
    if (this.btnZoom) {
      this.btnZoom.addEventListener('click', function () {
        self._toggleZoom();
      });
    }
  };

  CatalogArchive.prototype._toggleZoom = function () {
    this.zoomActive = !this.zoomActive;
    if (this.btnZoom) {
      this.btnZoom.classList.toggle('is-active', this.zoomActive);
    }
    if (this.zoomActive) {
      this._createMagnifier();
    } else {
      this._destroyMagnifier();
    }
  };

  CatalogArchive.prototype._createMagnifier = function () {
    if (this.magnifier) return;
    var mag = document.createElement('div');
    mag.className = 'catalog-archive__magnifier';
    mag.style.display = 'none';
    var magCanvas = document.createElement('canvas');
    magCanvas.width = MAGNIFIER_SIZE;
    magCanvas.height = MAGNIFIER_SIZE;
    mag.appendChild(magCanvas);
    this.canvasWrap.appendChild(mag);
    this.magnifier = mag;
    this.magCanvas = magCanvas;
    this.magCtx = magCanvas.getContext('2d');

    // Render hi-res version for zoom
    this._renderHiRes();

    // Hide click zones so they don't block events
    this._setClickZonesVisible(false);

    var self = this;
    this._magMoveHandler = function (e) { self._moveMagnifier(e); };
    this._magLeaveHandler = function () { mag.style.display = 'none'; };
    this._magTouchHandler = function (e) {
      e.preventDefault();
      var touch = e.touches[0];
      self._moveMagnifier(touch);
    };
    this._magTouchEnd = function () { mag.style.display = 'none'; };

    // Listen on canvasWrap so click zones don't block
    this.canvasWrap.addEventListener('mousemove', this._magMoveHandler);
    this.canvasWrap.addEventListener('mouseleave', this._magLeaveHandler);
    this.canvasWrap.addEventListener('touchmove', this._magTouchHandler, { passive: false });
    this.canvasWrap.addEventListener('touchend', this._magTouchEnd);
    this.canvasWrap.style.cursor = 'crosshair';
  };

  CatalogArchive.prototype._destroyMagnifier = function () {
    if (this.magnifier) {
      this.magnifier.remove();
      this.magnifier = null;
      this.magCanvas = null;
      this.magCtx = null;
    }
    if (this._magMoveHandler) {
      this.canvasWrap.removeEventListener('mousemove', this._magMoveHandler);
      this.canvasWrap.removeEventListener('mouseleave', this._magLeaveHandler);
      this.canvasWrap.removeEventListener('touchmove', this._magTouchHandler);
      this.canvasWrap.removeEventListener('touchend', this._magTouchEnd);
    }
    this.canvasWrap.style.cursor = '';
    this.hiResCanvas = null;
    this._setClickZonesVisible(true);
  };

  CatalogArchive.prototype._setClickZonesVisible = function (visible) {
    var prev = this.el.querySelector('[data-catalog-zone-prev]');
    var next = this.el.querySelector('[data-catalog-zone-next]');
    var val = visible ? '' : 'none';
    if (prev) prev.style.display = val;
    if (next) next.style.display = val;
  };

  CatalogArchive.prototype._renderHiRes = function () {
    var self = this;
    if (!this.pdfDoc) return;
    var pageNum = this.currentPage;
    this.pdfDoc.getPage(pageNum).then(function (page) {
      var vp1 = page.getViewport({ scale: 1 });
      var hiScale = MAGNIFIER_ZOOM * 1.5;
      var vp = page.getViewport({ scale: hiScale });
      var hc = document.createElement('canvas');
      hc.width = vp.width;
      hc.height = vp.height;
      var hctx = hc.getContext('2d');
      page.render({ canvasContext: hctx, viewport: vp }).promise.then(function () {
        self.hiResCanvas = hc;
      });
    });
  };

  CatalogArchive.prototype._moveMagnifier = function (e) {
    if (!this.magnifier || !this.hiResCanvas) return;
    var rect = this.canvas.getBoundingClientRect();
    var clientX = e.clientX !== undefined ? e.clientX : e.pageX;
    var clientY = e.clientY !== undefined ? e.clientY : e.pageY;
    var x = clientX - rect.left;
    var y = clientY - rect.top;

    // Only show when cursor is over the canvas area
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      this.magnifier.style.display = 'none';
      return;
    }

    // Position magnifier centered on cursor within wrap
    var wrapRect = this.canvasWrap.getBoundingClientRect();
    var magX = clientX - wrapRect.left - MAGNIFIER_SIZE / 2;
    var magY = clientY - wrapRect.top - MAGNIFIER_SIZE / 2;
    this.magnifier.style.display = 'block';
    this.magnifier.style.left = magX + 'px';
    this.magnifier.style.top = magY + 'px';

    // Map cursor position on displayed canvas to hi-res canvas coordinates
    var scaleX = this.hiResCanvas.width / rect.width;
    var scaleY = this.hiResCanvas.height / rect.height;
    // Source region: centered on cursor, size = MAGNIFIER_SIZE (at display pixels)
    var srcW = MAGNIFIER_SIZE;
    var srcH = MAGNIFIER_SIZE;
    var srcX = x * scaleX - srcW / 2;
    var srcY = y * scaleY - srcH / 2;

    this.magCtx.clearRect(0, 0, MAGNIFIER_SIZE, MAGNIFIER_SIZE);
    this.magCtx.drawImage(
      this.hiResCanvas,
      srcX, srcY, srcW, srcH,
      0, 0, MAGNIFIER_SIZE, MAGNIFIER_SIZE
    );
  };

  /* =============================================
     OPEN / RENDER
     ============================================= */
  CatalogArchive.prototype._openBook = function (pdfUrl, title) {
    var self = this;
    this.mobileMode = isMobile();
    var load = window.pdfjsLib
      ? Promise.resolve()
      : this._loadScript(PDFJS_CDN).then(function () {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
        });

    load.then(function () {
      if (self.shelfView) self.shelfView.classList.add('is-leaving');
      return self._wait(200);
    }).then(function () {
      if (self.shelfView) self.shelfView.style.display = 'none';
      if (self.readerTitleEl) self.readerTitleEl.textContent = title;
      if (self.readerView) self.readerView.classList.add('is-active');
      if (self.loadingEl) self.loadingEl.style.display = 'flex';
      if (self.canvasWrap) self.canvasWrap.style.display = 'none';
      return window.pdfjsLib.getDocument(pdfUrl).promise;
    }).then(function (pdf) {
      self.pdfDoc = pdf;
      self.totalPages = pdf.numPages;
      self.currentPage = 1;
      self.mobileHalf = 0;
      self.mobileVirtualPage = 0;
      self.mobileTotalVirtual = self.totalPages * 2;
      self._syncSlider();
      if (self.loadingEl) self.loadingEl.style.display = 'none';
      if (self.canvasWrap) self.canvasWrap.style.display = 'flex';
      self._renderCurrentView();
    }).catch(function (err) {
      console.error('PDF load error:', err);
      if (self.loadingEl) {
        var t = self.loadingEl.querySelector('.catalog-archive__loading-text');
        if (t) t.textContent = 'Could not load catalog.';
      }
    });
  };

  CatalogArchive.prototype._syncSlider = function () {
    if (!this.pageSlider) return;
    if (this.mobileMode) {
      this.pageSlider.max = this.mobileTotalVirtual;
      this.pageSlider.value = this.mobileVirtualPage + 1;
    } else {
      this.pageSlider.max = this.totalPages;
      this.pageSlider.value = this.currentPage;
    }
  };

  CatalogArchive.prototype._renderCurrentView = function () {
    if (this.mobileMode) {
      this._renderMobileHalf();
    } else {
      this._renderPage(this.currentPage);
    }
  };

  /* Render full spread to main canvas (desktop) */
  CatalogArchive.prototype._renderPage = function (pageNum) {
    if (this.rendering) { this.pendingPage = pageNum; return; }
    this.rendering = true;
    var self = this;
    this.pdfDoc.getPage(pageNum).then(function (page) {
      var cW = self.canvasWrap.clientWidth - 32;
      var vp1 = page.getViewport({ scale: 1 });
      var s = Math.min(cW / vp1.width, (window.innerHeight * 0.7) / vp1.height);
      var vp = page.getViewport({ scale: Math.max(s, 0.5) });
      self.lastViewport = vp;
      self.canvas.width = vp.width;
      self.canvas.height = vp.height;
      return page.render({ canvasContext: self.ctx, viewport: vp }).promise;
    }).then(function () {
      self._updateControls();
      self.rendering = false;
      if (self.hiResCanvas && self.zoomActive) self._renderHiRes();
      if (self.pendingPage !== null) {
        var p = self.pendingPage;
        self.pendingPage = null;
        self._renderPage(p);
      }
    }).catch(function () {
      self.rendering = false;
    });
  };

  /* Render half-page for mobile (left or right half) */
  CatalogArchive.prototype._renderMobileHalf = function () {
    if (this.rendering) return;
    this.rendering = true;
    var self = this;
    var pageNum = this.currentPage;
    var half = this.mobileHalf; // 0=left, 1=right

    this.pdfDoc.getPage(pageNum).then(function (page) {
      // Render at higher scale since we show only half
      var cW = self.canvasWrap.clientWidth - 20;
      var vp1 = page.getViewport({ scale: 1 });
      // Scale to fit half-width into full container width
      var halfWidth = vp1.width / 2;
      var s = Math.min(cW / halfWidth, (window.innerHeight * 0.75) / vp1.height);
      var vp = page.getViewport({ scale: Math.max(s, 0.5) });

      // Render full page to temp canvas, then crop
      var tmp = document.createElement('canvas');
      tmp.width = vp.width;
      tmp.height = vp.height;
      var tmpCtx = tmp.getContext('2d');
      return page.render({ canvasContext: tmpCtx, viewport: vp }).promise.then(function () {
        var cropW = Math.round(vp.width / 2);
        var srcX = half === 0 ? 0 : cropW;
        self.canvas.width = cropW;
        self.canvas.height = vp.height;
        self.ctx.drawImage(tmp, srcX, 0, cropW, vp.height, 0, 0, cropW, vp.height);
      });
    }).then(function () {
      self._updateControls();
      self.rendering = false;
      if (self.hiResCanvas && self.zoomActive) self._renderHiRes();
    }).catch(function () {
      self.rendering = false;
    });
  };

  /* Render a page to an offscreen canvas and return a data URL */
  CatalogArchive.prototype._prerenderPage = function (pageNum) {
    var self = this;
    var off = document.createElement('canvas');
    var offCtx = off.getContext('2d');
    return this.pdfDoc.getPage(pageNum).then(function (page) {
      var cW = self.canvasWrap.clientWidth - 32;
      var vp1 = page.getViewport({ scale: 1 });
      var s = Math.min(cW / vp1.width, (window.innerHeight * 0.7) / vp1.height);
      var vp = page.getViewport({ scale: Math.max(s, 0.5) });
      off.width = vp.width;
      off.height = vp.height;
      return page.render({ canvasContext: offCtx, viewport: vp }).promise.then(function () {
        return { canvas: off, url: off.toDataURL() };
      });
    });
  };

  CatalogArchive.prototype._changePage = function (delta) {
    if (this.turning) return;
    if (this.mobileMode) {
      this._changeMobilePage(delta);
    } else {
      var p = this.currentPage + delta;
      if (p < 1 || p > this.totalPages) return;
      this._doPageTurn(delta > 0, p);
    }
  };

  CatalogArchive.prototype._changeMobilePage = function (delta) {
    var next = this.mobileVirtualPage + delta;
    if (next < 0 || next >= this.mobileTotalVirtual) return;
    this.mobileVirtualPage = next;
    this.currentPage = Math.floor(next / 2) + 1;
    this.mobileHalf = next % 2;
    this._renderCurrentView();
  };

  CatalogArchive.prototype._goToPage = function (p) {
    if (p < 1 || p > this.totalPages || p === this.currentPage || this.turning) return;
    this._doPageTurn(p > this.currentPage, p);
  };

  CatalogArchive.prototype._goToFirst = function () {
    if (this.mobileMode) {
      this.mobileVirtualPage = 0;
      this.currentPage = 1;
      this.mobileHalf = 0;
      this._renderCurrentView();
    } else {
      this._goToPage(1);
    }
  };

  CatalogArchive.prototype._goToLast = function () {
    if (this.mobileMode) {
      this.mobileVirtualPage = this.mobileTotalVirtual - 1;
      this.currentPage = this.totalPages;
      this.mobileHalf = 1;
      this._renderCurrentView();
    } else {
      this._goToPage(this.totalPages);
    }
  };

  /* =============================================
     CENTER-FOLD PAGE TURN (desktop only)
     ============================================= */
  CatalogArchive.prototype._doPageTurn = function (forward, targetPage) {
    this.turning = true;
    var self = this;

    var oldUrl;
    try { oldUrl = this.canvas.toDataURL(); } catch (e) { oldUrl = null; }
    if (!oldUrl) {
      this.currentPage = targetPage;
      this._syncSlider();
      this._renderPage(targetPage);
      this.turning = false;
      return;
    }

    this._prerenderPage(targetPage).then(function (result) {
      var cRect = self.canvas.getBoundingClientRect();
      var wRect = self.canvasWrap.getBoundingClientRect();
      var ox = cRect.left - wRect.left;
      var oy = cRect.top - wRect.top;
      var cw = cRect.width;
      var ch = cRect.height;
      self._runCenterFold(forward, oldUrl, result.url, result.canvas, ox, oy, cw, ch, targetPage);
    }).catch(function () {
      self.currentPage = targetPage;
      self._syncSlider();
      self._renderPage(targetPage);
      self.turning = false;
    });
  };

  CatalogArchive.prototype._runCenterFold = function (forward, oldUrl, newUrl, offCanvas, ox, oy, cw, ch, targetPage) {
    var self = this;
    var halfW = Math.round(cw / 2);

    this.canvas.style.visibility = 'hidden';

    var cont = document.createElement('div');
    cont.style.cssText =
      'position:absolute;pointer-events:none;z-index:20;' +
      'top:' + oy + 'px;left:' + ox + 'px;width:' + cw + 'px;height:' + ch + 'px;';

    var newImg = new Image();
    newImg.src = newUrl;
    newImg.style.cssText = 'position:absolute;top:0;left:0;width:' + cw + 'px;height:' + ch + 'px;display:block;';
    cont.appendChild(newImg);

    var staticSide, turnZone, card, shadow;

    if (forward) {
      staticSide = this._makeClippedHalf(oldUrl, cw, ch, 'left');
      staticSide.style.zIndex = '1';
      cont.appendChild(staticSide);

      turnZone = document.createElement('div');
      turnZone.style.cssText =
        'position:absolute;top:0;left:' + halfW + 'px;width:' + halfW + 'px;height:' + ch + 'px;' +
        'perspective:2400px;z-index:2;overflow:visible;';

      card = document.createElement('div');
      card.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'transform-style:preserve-3d;transform-origin:left center;';

      var front = document.createElement('div');
      front.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'overflow:hidden;backface-visibility:hidden;background:#fff;';
      var fImg = new Image();
      fImg.src = oldUrl;
      fImg.style.cssText = 'position:absolute;top:0;left:-' + halfW + 'px;width:' + cw + 'px;height:' + ch + 'px;';
      front.appendChild(fImg);

      var back = document.createElement('div');
      back.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'backface-visibility:hidden;transform:rotateY(180deg);overflow:hidden;background:#f5f0e8;';
      var bImg = new Image();
      bImg.src = newUrl;
      bImg.style.cssText = 'position:absolute;top:0;left:0;width:' + cw + 'px;height:' + ch + 'px;';
      back.appendChild(bImg);
      var crease = document.createElement('div');
      crease.style.cssText =
        'position:absolute;top:0;left:0;width:6px;height:100%;z-index:1;' +
        'background:linear-gradient(to right,rgba(0,0,0,0.15),transparent);';
      back.appendChild(crease);

      card.appendChild(front);
      card.appendChild(back);
      turnZone.appendChild(card);
      cont.appendChild(turnZone);

      shadow = document.createElement('div');
      shadow.style.cssText =
        'position:absolute;top:0;left:' + halfW + 'px;width:' + halfW + 'px;height:' + ch + 'px;' +
        'z-index:3;pointer-events:none;opacity:0;' +
        'background:linear-gradient(to right,rgba(0,0,0,0.3) 0%,rgba(0,0,0,0.05) 30%,transparent 60%);';
      cont.appendChild(shadow);

    } else {
      staticSide = this._makeClippedHalf(oldUrl, cw, ch, 'right');
      staticSide.style.zIndex = '1';
      cont.appendChild(staticSide);

      turnZone = document.createElement('div');
      turnZone.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'perspective:2400px;z-index:2;overflow:visible;';

      card = document.createElement('div');
      card.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'transform-style:preserve-3d;transform-origin:right center;';

      var front = document.createElement('div');
      front.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'overflow:hidden;backface-visibility:hidden;background:#fff;';
      var fImg = new Image();
      fImg.src = oldUrl;
      fImg.style.cssText = 'position:absolute;top:0;left:0;width:' + cw + 'px;height:' + ch + 'px;';
      front.appendChild(fImg);

      var back = document.createElement('div');
      back.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'backface-visibility:hidden;transform:rotateY(180deg);overflow:hidden;background:#f5f0e8;';
      var bImg = new Image();
      bImg.src = newUrl;
      bImg.style.cssText = 'position:absolute;top:0;left:-' + halfW + 'px;width:' + cw + 'px;height:' + ch + 'px;';
      back.appendChild(bImg);
      var crease = document.createElement('div');
      crease.style.cssText =
        'position:absolute;top:0;right:0;width:6px;height:100%;z-index:1;' +
        'background:linear-gradient(to left,rgba(0,0,0,0.15),transparent);';
      back.appendChild(crease);

      card.appendChild(front);
      card.appendChild(back);
      turnZone.appendChild(card);
      cont.appendChild(turnZone);

      shadow = document.createElement('div');
      shadow.style.cssText =
        'position:absolute;top:0;left:0;width:' + halfW + 'px;height:' + ch + 'px;' +
        'z-index:3;pointer-events:none;opacity:0;' +
        'background:linear-gradient(to left,rgba(0,0,0,0.3) 0%,rgba(0,0,0,0.05) 30%,transparent 60%);';
      cont.appendChild(shadow);
    }

    this.canvasWrap.appendChild(cont);

    var maxAngle = forward ? -180 : 180;
    var startTime = null;

    function tick(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / TURN_MS, 1);
      var t = easeInOut(progress);

      card.style.transform = 'rotateY(' + (t * maxAngle) + 'deg)';
      shadow.style.opacity = Math.sin(t * Math.PI) * 0.7;

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        self.canvas.width = offCanvas.width;
        self.canvas.height = offCanvas.height;
        self.ctx.drawImage(offCanvas, 0, 0);
        self.currentPage = targetPage;
        self._syncSlider();
        self._updateControls();
        if (self.zoomActive) self._renderHiRes();

        cont.remove();
        self.canvas.style.visibility = '';
        self.turning = false;
      }
    }

    requestAnimationFrame(tick);
  };

  CatalogArchive.prototype._makeClippedHalf = function (imgUrl, fullW, fullH, side) {
    var halfW = Math.round(fullW / 2);
    var wrapper = document.createElement('div');
    wrapper.style.cssText =
      'position:absolute;top:0;width:' + halfW + 'px;height:' + fullH + 'px;overflow:hidden;' +
      (side === 'left' ? 'left:0;' : 'left:' + halfW + 'px;');
    var img = new Image();
    img.src = imgUrl;
    img.style.cssText =
      'position:absolute;top:0;width:' + fullW + 'px;height:' + fullH + 'px;' +
      (side === 'left' ? 'left:0;' : 'left:-' + halfW + 'px;');
    wrapper.appendChild(img);
    return wrapper;
  };

  CatalogArchive.prototype._updateControls = function () {
    if (this.mobileMode) {
      var vp = this.mobileVirtualPage + 1;
      var vt = this.mobileTotalVirtual;
      if (this.pageInfo) {
        this.pageInfo.innerHTML = 'Page <span>' + vp + '</span> of <span>' + vt + '</span>';
      }
      if (this.btnPrev) this.btnPrev.disabled = vp <= 1;
      if (this.btnFirst) this.btnFirst.disabled = vp <= 1;
      if (this.btnNext) this.btnNext.disabled = vp >= vt;
      if (this.btnLast) this.btnLast.disabled = vp >= vt;
      this._syncSlider();
    } else {
      if (this.pageInfo) {
        this.pageInfo.innerHTML = 'Page <span>' + this.currentPage + '</span> of <span>' + this.totalPages + '</span>';
      }
      if (this.btnPrev) this.btnPrev.disabled = this.currentPage <= 1;
      if (this.btnFirst) this.btnFirst.disabled = this.currentPage <= 1;
      if (this.btnNext) this.btnNext.disabled = this.currentPage >= this.totalPages;
      if (this.btnLast) this.btnLast.disabled = this.currentPage >= this.totalPages;
      this._syncSlider();
    }
  };

  CatalogArchive.prototype._closeReader = function () {
    if (this.zoomActive) this._toggleZoom();
    if (this.readerView) this.readerView.classList.remove('is-active');
    if (this.shelfView) {
      this.shelfView.style.display = '';
      this.shelfView.classList.remove('is-leaving');
    }
    this.pdfDoc = null;
    this.currentPage = 1;
    this.totalPages = 0;
  };

  CatalogArchive.prototype._loadScript = function (src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  };

  CatalogArchive.prototype._wait = function (ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  };

  function init() {
    var el = document.querySelector('[data-catalog-archive]');
    if (el) new CatalogArchive(el);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
